/**
 * StellungnahmeLabel — Wiederverwendbares Label für Stellungnahmen
 * Issue #240 — Teil von #239 (Stellungnahmen-Modul)
 *
 * Exportiert:
 *  - STELLUNGNAHME_LABEL / STELLUNGNAHME_COLOR: Konstanten
 *  - hasStellungnahmeLabel(labels): Helper zum Prüfen der Array-Zugehörigkeit
 *  - StellungnahmeBadge: rot hervorgehobene Pille für Anzeige
 *  - StellungnahmeToggle: Button zum Setzen/Entfernen des Labels
 */

import { Scale } from 'lucide-react';

export const STELLUNGNAHME_LABEL = 'Stellungnahme';
export const STELLUNGNAHME_COLOR = '#EF4444';

export function hasStellungnahmeLabel(labels: string[] | null | undefined): boolean {
  if (!labels) return false;
  return labels.includes(STELLUNGNAHME_LABEL);
}

// Pluralisierung gemäß Issue #240 AC-Beispiel „Stellungnahmen: 2": Singular bei count === 1.
export function formatStellungnahmenLabel(count: number): string {
  const base = count === 1 ? STELLUNGNAHME_LABEL : `${STELLUNGNAHME_LABEL}n`;
  return `${base}: ${count}`;
}

export function StellungnahmeBadge({
  count,
  label,
  className,
}: {
  count?: number;
  label?: string;
  className?: string;
}) {
  const hasCount = typeof count === 'number';
  const text = hasCount
    ? formatStellungnahmenLabel(count!)
    : (label ?? STELLUNGNAHME_LABEL);
  return (
    <span
      data-testid="stellungnahme-badge"
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${className ?? ''}`}
      style={{
        backgroundColor: STELLUNGNAHME_COLOR + '1A',
        color: STELLUNGNAHME_COLOR,
        border: `1px solid ${STELLUNGNAHME_COLOR}33`,
      }}
      title={text}
    >
      <Scale className="w-3 h-3" />
      <span>{text}</span>
    </span>
  );
}

export function StellungnahmeToggle({
  active,
  onToggle,
  disabled,
  'data-testid': testId,
}: {
  active: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={() => onToggle(!active)}
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors flex-shrink-0 ${
        active
          ? 'text-white border-transparent'
          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={active ? { backgroundColor: STELLUNGNAHME_COLOR } : undefined}
      title={active ? 'Stellungnahme-Label entfernen' : 'Stellungnahme-Label setzen'}
      aria-pressed={active}
    >
      <Scale className="w-3 h-3" />
      {STELLUNGNAHME_LABEL}
    </button>
  );
}
